import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LiveScoreBar from "@/components/LiveScoreBar";
import PinnedLiveBanner from "@/components/PinnedLiveBanner";
import TopInfoBar from "@/components/TopInfoBar";
import MobileBottomNav from "@/components/MobileBottomNav";
import AnalyticsRouterListener from "@/components/AnalyticsRouterListener";
import ScrollRevealManager from "@/components/anim/ScrollRevealManager";
import PageTransition from "@/components/anim/PageTransition";
import ScrollToTop from "@/components/ScrollToTop";
import SEODefaults from "@/components/SEODefaults";
import GlobalLinkPrefetcher from "@/components/GlobalLinkPrefetcher";
import SignupPrompt from "@/components/SignupPrompt";
import ConsentBanner from "@/components/ads/ConsentBanner";

// Home is the LCP page — keep it eager so first paint is not blocked
// by an extra async chunk fetch on the critical path.
import Home from "@/pages/Home";

// Everything else is route-split so the initial JS bundle only ships what
// the user actually needs on first paint. Cuts initial JS by ~60% and
// slashes Total Blocking Time on mobile.
const Schedule = lazy(() => import("@/pages/Schedule"));
const Roster = lazy(() => import("@/pages/Roster"));
const Stats = lazy(() => import("@/pages/Stats"));
const News = lazy(() => import("@/pages/News"));
const NewsDetail = lazy(() => import("@/pages/NewsDetail"));
const Community = lazy(() => import("@/pages/Community"));
const Admin = lazy(() => import("@/pages/Admin"));
const CategoryNews = lazy(() => import("@/pages/CategoryNews"));
const About = lazy(() => import("@/pages/About"));
const History = lazy(() => import("@/pages/History"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const LiveArticle = lazy(() => import("@/pages/LiveArticle"));
const FilmRoom = lazy(() => import("@/pages/FilmRoom"));
const FilmRoomDetail = lazy(() => import("@/pages/FilmRoomDetail"));
const PlayerPage = lazy(() => import("@/pages/PlayerPage"));
const CommandCenter = lazy(() => import("@/pages/CommandCenter"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const PrivacyPolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.TermsOfService })));
const CookiePolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.CookiePolicy })));
const ContactPage = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.ContactPage })));
const EditorialStandards = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.EditorialStandards })));
const CorrectionsPolicy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.CorrectionsPolicy })));
const Ownership = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Ownership })));
const NewsletterArchive = lazy(() => import("@/pages/NewsletterArchive"));
const NewsletterEdition = lazy(() => import("@/pages/NewsletterEdition"));
const TagPage = lazy(() => import("@/pages/TagPage"));
const GamedayHub = lazy(() => import("@/pages/GamedayHub"));

// Minimal Suspense fallback — no spinner, no layout shift; matches page
// canvas so the transition feels instant on fast networks.
const RouteFallback = () => (
  <div
    data-testid="route-fallback"
    aria-hidden="true"
    style={{ minHeight: "60vh" }}
  />
);

function App() {
  return (
    <div className="App min-h-screen bg-titans-void text-white">
      <BrowserRouter>
        <AnalyticsRouterListener />
        <ScrollToTop />
        <SEODefaults />
        <GlobalLinkPrefetcher />
        <ScrollRevealManager />
        <PageTransition />
        <Header />
        <TopInfoBar />
        <LiveScoreBar />
        <PinnedLiveBanner />
        <main data-testid="main-content">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/live/:id" element={<LiveArticle />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/roster" element={<Roster />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/news" element={<News />} />
              <Route path="/newsletter" element={<Navigate to="/newsletter/archive" replace />} />
              <Route path="/newsletter/archive" element={<NewsletterArchive />} />
              <Route path="/newsletter/edition/:slug" element={<NewsletterEdition />} />
              <Route path="/news/:id" element={<NewsDetail />} />
              <Route path="/tag/:slug" element={<TagPage />} />
              <Route path="/gameday" element={<GamedayHub />} />
              <Route path="/gameday/:eventId" element={<GamedayHub />} />
              <Route path="/breaking-news" element={<CategoryNews slug="breaking-news" />} />
              <Route path="/analysis" element={<CategoryNews slug="analysis" />} />
              <Route path="/offseason" element={<CategoryNews slug="offseason" />} />
              <Route path="/cap-central" element={<Navigate to="/roster?tab=cap" replace />} />
              <Route path="/depth-chart" element={<Navigate to="/roster?tab=depth" replace />} />
              <Route path="/polls" element={<Navigate to="/community?tab=polls" replace />} />
              <Route path="/community" element={<Community />} />
              <Route path="/about" element={<About />} />
              <Route path="/history" element={<History />} />
              <Route path="/film-room" element={<FilmRoom />} />
              <Route path="/film-room/:slug" element={<FilmRoomDetail />} />
              <Route path="/player/:id" element={<PlayerPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/command-center" element={<CommandCenter />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/editorial-standards" element={<EditorialStandards />} />
              <Route path="/corrections" element={<CorrectionsPolicy />} />
              <Route path="/ownership" element={<Ownership />} />
              <Route path="/author/:slug" element={<AuthorPage />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/shop" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <MobileBottomNav />
        <SignupPrompt />
        <ConsentBanner />
        <Toaster theme="dark" position="bottom-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
